import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import i18next from 'i18next';
import ReactHtmlParser from 'react-html-parser';

import { Tracking, TrackingLabels, TrackingTimeline } from '@shared/tracking-timeline';
import Loading from '~components/Spinner/components/loading';
import { getProjectTracking } from '~services/ProjectTrackingService';
import { getDate } from '~utils/date';

import styles from './styles.module.scss';
import './i18n';

// i18next 13 types `t` as string | object; every key used here is a plain string.
const t = (key: string, options?: object): string => String(i18next.t(key, options));

const trackingLabels = (): TrackingLabels => ({
  totalProgress: t('tracking:totalProgress'),
  subStage: t('tracking:subStage'),
  stages: t('tracking:stages'),
  subStages: t('tracking:subStages'),
  inProgressSince: date => t('tracking:inProgressSince', { date }),
  startedOn: date => t('tracking:startedOn', { date }),
  expectedEnd: date => t('tracking:expectedEnd', { date }),
  summary: (progress, stage) => t('tracking:summary', { progress, stage })
});

interface Props {
  projectId: string;
  compact?: boolean;
  showMarker?: boolean;
  /** Show the "Estado de situación" date and description under the timeline. */
  showStatus?: boolean;
  className?: string;
  /** Called once loaded, with null when the project has no Seguimiento (nothing is rendered then). */
  onLoad?: (tracking: Tracking | null) => void;
}

interface State {
  loading: boolean;
  tracking: Tracking | null;
}

/** Fetches a project's Seguimiento and renders its timeline; embeddable anywhere. */
function ProjectTracking({ projectId, compact, showMarker, showStatus = true, className, onLoad }: Props) {
  const [{ loading, tracking }, setState] = useState<State>({ loading: true, tracking: null });
  const labels = useMemo(trackingLabels, [i18next.language]);

  useEffect(() => {
    let active = true;
    setState({ loading: true, tracking: null });
    getProjectTracking(projectId).then(response => {
      if (!active) return;
      const loaded = response.ok && response.data && response.data.hasTracking ? response.data : null;
      setState({ loading: false, tracking: loaded });
      if (onLoad) onLoad(loaded);
    });
    return () => {
      active = false;
    };
    // `onLoad` is a notification, not an input: re-fetch only for another project.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (loading) return <Loading />;
  if (!tracking) return null;

  return (
    <div className={cx(styles.container, className)}>
      <TrackingTimeline tracking={tracking} compact={compact} showMarker={showMarker} labels={labels} />
      {showStatus && (
        <div className={styles.status}>
          <h3 className={styles.statusTitle}>
            {i18next.t('tracking:statusOn', { date: tracking.lastUpdate ? getDate(tracking.lastUpdate) : '-' })}
          </h3>
          {tracking.description && <div className={styles.description}>{ReactHtmlParser(tracking.description)}</div>}
        </div>
      )}
    </div>
  );
}

export default ProjectTracking;
